import type { CandidateSkillMatchMode, SavedSearchDraft, SearchFilterValue } from "./structured-search.data";
import { coerceStructuredSearchFilters, normalizeFiltersForComparison } from "./structured-search.filters";

export type StructuredSearchRouteSearch = {
  q?: unknown;
  filters?: unknown;
  candidateSkillMatchMode?: unknown;
  skillPresetId?: unknown;
  minSkillPresetCoveragePct?: unknown;
  sort?: unknown;
  savedSearchId?: unknown;
};

export type StructuredSearchUrlState = {
  query: string;
  filters: SearchFilterValue;
  candidateSkillMatchMode: CandidateSkillMatchMode;
  skillPresetId?: string;
  minSkillPresetCoveragePct?: number;
  sort: Array<{ id: string; desc: boolean }>;
  savedSearchId?: string;
};

export const defaultStructuredSearchUrlState: StructuredSearchUrlState = {
  query: "",
  filters: {},
  candidateSkillMatchMode: "any",
  skillPresetId: undefined,
  minSkillPresetCoveragePct: undefined,
  sort: [],
  savedSearchId: undefined,
};

export function structuredSearchRouteSearchParams(search: Record<string, unknown>): StructuredSearchRouteSearch {
  return search;
}

function getSingleSearchValue(value: unknown) {
  return Array.isArray(value) ? value[0] : value;
}

function getStringSearchValue(value: unknown) {
  const singleValue = getSingleSearchValue(value);
  return typeof singleValue === "string" ? singleValue.trim() : undefined;
}

function getNumberSearchValue(value: unknown) {
  const singleValue = getSingleSearchValue(value);
  const parsed =
    typeof singleValue === "number" ? singleValue : typeof singleValue === "string" ? Number(singleValue) : undefined;
  return typeof parsed === "number" && Number.isFinite(parsed) ? parsed : undefined;
}

function parseJsonSearchValue(value: unknown) {
  const singleValue = getSingleSearchValue(value);
  if (typeof singleValue !== "string") return singleValue;

  try {
    return JSON.parse(singleValue);
  } catch {
    return undefined;
  }
}

function normalizeStructuredSearchSort(input: unknown): Array<{ id: string; desc: boolean }> {
  if (!Array.isArray(input)) return [];

  const seen = new Set<string>();
  const sort: Array<{ id: string; desc: boolean }> = [];

  for (const item of input) {
    if (!item || typeof item !== "object") continue;
    const candidate = item as Record<string, unknown>;
    const id = typeof candidate.id === "string" ? candidate.id.trim() : "";
    if (!id || seen.has(id)) continue;
    seen.add(id);
    sort.push({ id, desc: candidate.desc === true });
  }

  return sort;
}

export function parseStructuredSearchUrlState(search: StructuredSearchRouteSearch = {}): StructuredSearchUrlState {
  const candidateSkillMatchMode = getStringSearchValue(search.candidateSkillMatchMode);
  const skillPresetId = getStringSearchValue(search.skillPresetId);
  const savedSearchId = getStringSearchValue(search.savedSearchId);

  return {
    query: getStringSearchValue(search.q) ?? "",
    filters: coerceStructuredSearchFilters(parseJsonSearchValue(search.filters)),
    candidateSkillMatchMode: candidateSkillMatchMode === "all" ? "all" : "any",
    skillPresetId: skillPresetId || undefined,
    minSkillPresetCoveragePct: getNumberSearchValue(search.minSkillPresetCoveragePct),
    sort: normalizeStructuredSearchSort(parseJsonSearchValue(search.sort)),
    savedSearchId: savedSearchId || undefined,
  };
}

export function buildStructuredSearchRouteSearch(state: StructuredSearchUrlState): StructuredSearchRouteSearch {
  const search: StructuredSearchRouteSearch = {};
  const query = state.query.trim();
  const filters = normalizeFiltersForComparison(state.filters);
  const sort = normalizeStructuredSearchSort(state.sort);

  if (query) {
    search.q = query;
  }

  if (Object.keys(filters).length > 0) {
    search.filters = JSON.stringify(filters);
  }

  if (state.candidateSkillMatchMode === "all") {
    search.candidateSkillMatchMode = "all";
  }

  if (state.skillPresetId) {
    search.skillPresetId = state.skillPresetId;
  }

  if (typeof state.minSkillPresetCoveragePct === "number" && Number.isFinite(state.minSkillPresetCoveragePct)) {
    search.minSkillPresetCoveragePct = state.minSkillPresetCoveragePct;
  }

  if (sort.length > 0) {
    search.sort = JSON.stringify(sort);
  }

  if (state.savedSearchId) {
    search.savedSearchId = state.savedSearchId;
  }

  return search;
}

export function getStructuredSearchUrlStateKey(state: StructuredSearchUrlState): string {
  return JSON.stringify(buildStructuredSearchRouteSearch(state));
}

export function getStructuredSearchCriteriaKey(state: StructuredSearchUrlState): string {
  return JSON.stringify(buildStructuredSearchRouteSearch({ ...state, savedSearchId: undefined }));
}

export function getSavedSearchRestoreKey({
  selectedEntityId,
  routeUrlStateKey,
}: {
  selectedEntityId: string | null | undefined;
  routeUrlStateKey: string;
}): string | null {
  if (!selectedEntityId) return null;
  return JSON.stringify({ selectedEntityId, routeUrlStateKey });
}

export function getSavedSearchUrlStateKey(search: SavedSearchDraft): string {
  return getStructuredSearchCriteriaKey({
    query: search.query,
    filters: search.filters,
    candidateSkillMatchMode: search.candidateSkillMatchMode,
    skillPresetId: search.skillPresetId,
    minSkillPresetCoveragePct: search.minSkillPresetCoveragePct,
    sort: search.sort || [],
  });
}

export function findMatchingSavedSearchId({
  savedSearches,
  selectedEntityId,
  routeUrlState,
}: {
  savedSearches: SavedSearchDraft[];
  selectedEntityId: string | null | undefined;
  routeUrlState: StructuredSearchUrlState;
}): string | null {
  if (!selectedEntityId) return null;
  const routeCriteriaKey = getStructuredSearchCriteriaKey(routeUrlState);
  if (routeUrlState.savedSearchId) {
    const savedSearch = savedSearches.find(
      (search) => search.entityId === selectedEntityId && search.id === routeUrlState.savedSearchId,
    );
    if (savedSearch && getSavedSearchUrlStateKey(savedSearch) === routeCriteriaKey) {
      return savedSearch.id;
    }
  }

  return (
    savedSearches.find(
      (search) => search.entityId === selectedEntityId && getSavedSearchUrlStateKey(search) === routeCriteriaKey,
    )?.id ?? null
  );
}
